import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseServiceService } from '../../services/base-service.service';
import { ModalNotifyComponent, ModalNotifyTypes } from '../modal-notify/modal-notify.component';
import {
  ReporteriaService,
  CatalogoVariableReporteDTO,
  ConfiguracionReporteColumnaResponseDTO,
  ReporteriaColumnaFrontendMeta,
  ReporteriaProcesoFrontendMeta,
  HistoricoSpConfiguracionDTO
} from '../../services/reporteria.service';

declare var fabric: any;

interface ColumnaEditando {
  NombreVariable: string;
  TipoDato: string;
  Origen: string;
  Etiqueta: string;
  Orden: number;
  VisibleEnTabla: boolean;
  TipoControl: 'input' | 'areatext';
  EsCampoNativo: boolean;
  ColumnaReferenciaVista?: string;
}

// El backend ya entrega los campos nativos (Estado, Tipo, UsuarioInicia, FechaInicia, FechaFinaliza
// y, en origen Vista, IdEjecucion) dentro del propio catálogo con EsCampoNativo = true, marcados con
// su nombre real (sin prefijo). Este set solo se usa como respaldo para datos guardados antiguos.
const NOMBRES_NATIVOS_CONOCIDOS = new Set([
  'IdEjecucion', 'Estado', 'Tipo', 'UsuarioInicia', 'FechaInicia', 'FechaFinaliza'
]);

// El backend aún no incluye IdEjecucion como campo nativo seleccionable en el catálogo de
// origen "Variables" (solo lo hace en origen "Vista"), así que se agrega aquí de forma local.
const CAMPO_ID_EJECUCION_VARIABLES: CatalogoVariableReporteDTO = {
  NombreVariable: 'IdEjecucion',
  TipoDato: 'Number',
  Origen: 'Nativo',
  EsCampoNativo: true
};

@Component({
  selector: 'app-reporteria-admin',
  templateUrl: './reporteria-admin.component.html',
  styleUrl: './reporteria-admin.component.css'
})
export class ReporteriaAdminComponent implements OnInit {
  @ViewChild(ModalNotifyComponent) modalNotifyComponent!: ModalNotifyComponent;

  botonProcesando = false;
  textoBotonProcesando = 'Guardando...';
  cargandoConfig = false;

  getDataTableProcesos: any = ['Reporteria', 'GetDataTableProcesoReporteria'];
  dtOptButtons: any = [];
  dtInstance: any = null;

  idProcesoSeleccionado: number | null = null;
  nombreProcesoSeleccionado = '';

  catalogoVariables: CatalogoVariableReporteDTO[] = [];
  columnasEditando: ColumnaEditando[] = [];

  // Vista origin state
  tipoOrigen: 'Variables' | 'Vista' = 'Variables';
  vistasDisponibles: string[] = [];
  vistaSeleccionada = '';
  cargandoCatalogo = false;

  habilitarBusquedaFechas = false;
  habilitarHistorico = false;
  modoHistorico: 'default' | 'sp' = 'default';
  historialSP: HistoricoSpConfiguracionDTO[] = [];
  historicoSpNombre = '';
  historicoSpPares: { VariableEntrada: string; VariableReporte: string }[] = [
    { VariableEntrada: '', VariableReporte: '' }
  ];
  columnaFechaVista = '';

  dialogComponentConfig: any = null;
  dialogComponentHistoricoSp: any = null;

  columnas = [
    { title: 'ID', data: 'Id' },
    { title: 'Nombre', data: 'Nombre' },
    {
      title: 'Reporte',
      data: 'TieneReporteConfigurado',
      orderable: false,
      render: (data: any, type: any) => {
        if (type === 'display') {
          if (data === true) {
            return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:#e8f5e9;color:#2e7d32;font-size:11px;font-weight:600;">Con reporte</span>';
          }
          return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:#f3f4f6;color:#666;font-size:11px;font-weight:600;">Sin reporte</span>';
        }
        return data ? 'Con reporte' : 'Sin reporte';
      }
    },
    { title: 'Estado', data: 'Estado' },
    { title: 'Versión', data: 'Version' },
    {
      title: 'Activo',
      data: 'Activo',
      orderable: false,
      render: (data: any, type: any) => {
        if (type === 'display') return `<input type="checkbox" disabled ${data ? 'checked' : ''}>`;
        return data;
      }
    }
  ];

  constructor(
    private reporteriaService: ReporteriaService,
    private baseService: BaseServiceService
  ) {}

  ngOnInit(): void {
    this.dtOptButtons.push({
      'button-id': 'btnConfigReporteria',
      'button-icon': 'ms-Icon--BarChart4',
      'button-tooltip': 'Configurar Reportería',
      'conditional-data': [{ key: 'nombre', value: 'Nombre' }]
    });
  }

  gridLoaded(event: any): void {
    this.dtInstance = event['table'];
  }

  async customClick(event: any): Promise<void> {
    if (event['button-id'] === 'btnConfigReporteria') {
      this.idProcesoSeleccionado = Number(event['data-id']);
      this.nombreProcesoSeleccionado = event['data-nombre'] || '';
      await this.abrirModalConfiguracion();
    }
  }

  async abrirModalConfiguracion(): Promise<void> {
    this.safeCloseDialog(); // reset any leftover instance before reopening
    this.cargandoConfig = true;
    this.tipoOrigen = 'Variables';
    this.vistaSeleccionada = '';
    this.habilitarBusquedaFechas = false;
    this.habilitarHistorico = false;
    this.modoHistorico = 'default';
    this.historialSP = [];
    this.historicoSpNombre = '';
    this.historicoSpPares = [{ VariableEntrada: '', VariableReporte: '' }];
    this.columnaFechaVista = '';
    this.catalogoVariables = [];
    this.columnasEditando = [];
    this.vistasDisponibles = [];

    const dialog = document.querySelector('.modal-config-reporteria');
    this.dialogComponentConfig = new fabric['Dialog'](dialog);
    this.dialogComponentConfig.open();

    try {
      const metaFrontend = this.obtenerFrontendMeta();

      // Stage 1: vistas disponibles + config existente en paralelo
      const [resVistas, resConfig] = await Promise.all([
        this.reporteriaService.getVistasDisponibles(),
        this.reporteriaService.getConfiguracion(this.idProcesoSeleccionado!)
      ]);

      if (resVistas.Ok) {
        this.vistasDisponibles = (resVistas.Data || []).map((v: any) => v.NombreVista as string);
      }

      // Restaurar tipoOrigen y vistaSeleccionada de la config guardada
      if (resConfig.Ok && resConfig.Data?.length > 0) {
        const first = resConfig.Data[0] as ConfiguracionReporteColumnaResponseDTO & { ModoHistorico?: 'default' | 'sp'; ConfiguracionHistoricoSp?: HistoricoSpConfiguracionDTO[] };
        this.tipoOrigen = first.TipoOrigen || 'Variables';
        this.vistaSeleccionada = first.NombreVista || '';
        this.habilitarHistorico = !!first.HabilitarHistorico;
        this.modoHistorico = this.tipoOrigen === 'Vista' ? 'sp' : (first.ModoHistorico || 'default');
        this.historialSP = first.ConfiguracionHistoricoSp || [];
      }

      if (metaFrontend) {
        this.habilitarBusquedaFechas = !!metaFrontend.HabilitarBusquedaFechas;
        this.columnaFechaVista = metaFrontend.ColumnaFechaVista || '';
        this.habilitarHistorico = !!metaFrontend.HabilitarHistorico;
        this.modoHistorico = this.tipoOrigen === 'Vista' ? 'sp' : (metaFrontend.ModoHistorico || this.modoHistorico);
        this.historialSP = metaFrontend.ConfiguracionHistoricoSp || this.historialSP;
        this.tipoOrigen = metaFrontend.TipoOrigen || this.tipoOrigen;
        if (this.tipoOrigen === 'Vista') {
          this.vistaSeleccionada = metaFrontend.NombreVista || this.vistaSeleccionada;
          this.habilitarHistorico = true;
          this.modoHistorico = 'sp';
        }
      }

      // Stage 2: catálogo según tipoOrigen
      await this.cargarCatalogoActual();

      // Poblar columnas editando desde config existente + metadata local
      this.hidratarColumnasEditando(
        (resConfig.Ok ? (resConfig.Data || []) : []) as ConfiguracionReporteColumnaResponseDTO[],
        metaFrontend
      );
    } catch (error: any) {
      this.baseService.triggerShowModal('Error', 'No se pudo cargar la configuración del proceso.', ModalNotifyTypes.Danger, false);
      this.safeCloseDialog();
    } finally {
      this.cargandoConfig = false;
    }
  }

  onTipoOrigenChange(): void {
    this.vistaSeleccionada = '';
    this.columnaFechaVista = '';
    this.catalogoVariables = [];
    this.columnasEditando = [];
    if (this.tipoOrigen === 'Vista' && this.habilitarHistorico) {
      this.modoHistorico = 'sp';
    }
    this.cargarCatalogoActual();
  }

  onHabilitarHistoricoChange(): void {
    if (this.tipoOrigen === 'Vista' && this.habilitarHistorico) {
      this.modoHistorico = 'sp';
    }
  }

  async cargarCatalogoActual(): Promise<void> {
    this.cargandoCatalogo = true;
    this.catalogoVariables = [];
    try {
      let catalogoBase: CatalogoVariableReporteDTO[] = [];
      if (this.tipoOrigen === 'Vista') {
        if (this.vistaSeleccionada) {
          const resVista = await this.reporteriaService.getColumnasVista(this.vistaSeleccionada);
          if (resVista.Ok) {
            catalogoBase = resVista.Data || [];
          } else {
            this.baseService.triggerShowModal('Error', resVista.Error || 'No se pudieron cargar las columnas de la vista.', ModalNotifyTypes.Danger, false);
          }
        }
      } else {
        const resVar = await this.reporteriaService.getCatalogoVariables(this.idProcesoSeleccionado!);
        if (resVar.Ok) {
          catalogoBase = resVar.Data || [];
        } else {
          this.baseService.triggerShowModal('Error', resVar.Error || 'No se pudieron cargar las variables del proceso.', ModalNotifyTypes.Danger, false);
        }
      }

      // El backend ya incluye los campos nativos dentro del propio catálogo (EsCampoNativo = true),
      // por lo que no se debe agregar aquí una lista sintética adicional (eso causaba duplicados).
      // La única excepción es IdEjecucion en origen Variables, que el backend aún no expone ahí.
      const listaCompleta = this.tipoOrigen === 'Variables'
        ? [CAMPO_ID_EJECUCION_VARIABLES, ...catalogoBase]
        : catalogoBase;
      const mapa = new Map<string, CatalogoVariableReporteDTO>();
      listaCompleta.forEach(item => mapa.set(item.NombreVariable, item));
      this.catalogoVariables = Array.from(mapa.values());
    } catch {
      this.baseService.triggerShowModal('Error', 'No se pudieron cargar las variables/columnas para configurar el reporte.', ModalNotifyTypes.Danger, false);
    } finally {
      this.cargandoCatalogo = false;
    }
  }

  async onVistaChange(): Promise<void> {
    this.columnasEditando = [];
    await this.cargarCatalogoActual();
  }

  isVariableSeleccionada(nombreVariable: string): boolean {
    return this.columnasEditando.some(c => c.NombreVariable === nombreVariable);
  }

  toggleVariable(variable: CatalogoVariableReporteDTO, checked: boolean): void {
    if (checked) {
      if (!this.isVariableSeleccionada(variable.NombreVariable)) {
        this.columnasEditando.push({
          NombreVariable: variable.NombreVariable,
          TipoDato: variable.TipoDato,
          Origen: variable.Origen,
          Etiqueta: this.obtenerEtiquetaDefault(variable.NombreVariable),
          Orden: this.columnasEditando.length,
          VisibleEnTabla: true,
          TipoControl: 'input',
          EsCampoNativo: this.esCampoNativoVariable(variable),
          ColumnaReferenciaVista: ''
        });
      }
    } else {
      this.columnasEditando = this.columnasEditando.filter(c => c.NombreVariable !== variable.NombreVariable);
      this.columnasEditando.forEach((c, i) => c.Orden = i);
    }
  }

  quitarColumna(col: ColumnaEditando): void {
    this.columnasEditando = this.columnasEditando.filter(c => c.NombreVariable !== col.NombreVariable);
    this.columnasEditando.forEach((c, i) => c.Orden = i);
  }

  moverArriba(index: number): void {
    if (index > 0) {
      [this.columnasEditando[index], this.columnasEditando[index - 1]] =
        [this.columnasEditando[index - 1], this.columnasEditando[index]];
      this.columnasEditando.forEach((c, i) => c.Orden = i);
    }
  }

  moverAbajo(index: number): void {
    if (index < this.columnasEditando.length - 1) {
      [this.columnasEditando[index], this.columnasEditando[index + 1]] =
        [this.columnasEditando[index + 1], this.columnasEditando[index]];
      this.columnasEditando.forEach((c, i) => c.Orden = i);
    }
  }

  agregarParHistoricoSp(): void {
    this.historicoSpPares.push({ VariableEntrada: '', VariableReporte: '' });
  }

  quitarParHistoricoSp(index: number): void {
    if (this.historicoSpPares.length <= 1) return;
    this.historicoSpPares.splice(index, 1);
  }

  puedeGuardarHistoricoSp(): boolean {
    return !!this.historicoSpNombre?.trim() &&
      this.historicoSpPares.some(p => !!p.VariableEntrada?.trim() && !!p.VariableReporte?.trim());
  }

  agregarConfiguracionHistoricoSp(): void {
    const nombreSp = this.historicoSpNombre?.trim();
    if (!nombreSp) {
      this.baseService.triggerShowModal('Validación', 'Debe indicar el nombre del SP.', ModalNotifyTypes.Warning, false);
      return;
    }

    const paresValidos = this.historicoSpPares.filter(p => p.VariableEntrada?.trim() && p.VariableReporte?.trim());
    if (paresValidos.length === 0) {
      this.baseService.triggerShowModal(
        'Validación',
        'Debe completar al menos una variable de entrada y su variable del reporte asociada.',
        ModalNotifyTypes.Warning,
        false
      );
      return;
    }

    const yaExiste = paresValidos.some(p =>
      this.historialSP.some(sp => sp.NombreSp === nombreSp && sp.VariableReporte === p.VariableReporte)
    );
    if (yaExiste) {
      this.baseService.triggerShowModal('Validación', 'Ya existe una configuración de este SP para alguna de esas variables del reporte.', ModalNotifyTypes.Warning, false);
      return;
    }

    paresValidos.forEach(p => {
      this.historialSP.push({ NombreSp: nombreSp, VariableEntrada: p.VariableEntrada.trim(), VariableReporte: p.VariableReporte });
    });

    this.historicoSpNombre = '';
    this.historicoSpPares = [{ VariableEntrada: '', VariableReporte: '' }];
    this.cerrarModalHistoricoSp();
  }

  quitarConfiguracionHistoricoSp(index: number): void {
    this.historialSP.splice(index, 1);
  }

  abrirModalHistoricoSp(): void {
    this.historicoSpNombre = '';
    this.historicoSpPares = [{ VariableEntrada: '', VariableReporte: '' }];
    const dialog = document.querySelector('.modal-historico-sp');
    this.dialogComponentHistoricoSp = new fabric['Dialog'](dialog);
    this.dialogComponentHistoricoSp.open();
  }

  cerrarModalHistoricoSp(): void {
    try {
      this.dialogComponentHistoricoSp?.close();
    } catch {
      // Fabric removeChild puede fallar si el diálogo ya fue desmontado del DOM.
    } finally {
      this.dialogComponentHistoricoSp = null;
    }
  }

  async guardarConfiguracion(): Promise<void> {
    if (!this.idProcesoSeleccionado) return;
    if (this.columnasEditando.length === 0) {
      this.baseService.triggerShowModal('Validación', 'Debe seleccionar al menos una variable como columna.', ModalNotifyTypes.Warning, false);
      return;
    }

    if (this.tipoOrigen === 'Vista' && !this.vistaSeleccionada) {
      this.baseService.triggerShowModal('Validación', 'Debe seleccionar una vista para configurar el reporte.', ModalNotifyTypes.Warning, false);
      return;
    }

    if (this.tipoOrigen === 'Vista' && this.habilitarBusquedaFechas && !this.columnaFechaVista.trim()) {
      this.baseService.triggerShowModal('Validación', 'Debe indicar el nombre de la columna de fecha para filtrar en modo Vista.', ModalNotifyTypes.Warning, false);
      return;
    }

    if (this.habilitarHistorico && this.tipoOrigen === 'Vista' && this.modoHistorico !== 'sp') {
      this.modoHistorico = 'sp';
    }

    const camposNativosSinReferencia = this.columnasEditando.filter(
      c => c.EsCampoNativo && this.tipoOrigen === 'Vista' && !c.ColumnaReferenciaVista?.trim()
    );
    if (camposNativosSinReferencia.length > 0) {
      this.baseService.triggerShowModal(
        'Validación',
        'Todos los campos nativos seleccionados en modo Vista deben tener una columna de referencia.',
        ModalNotifyTypes.Warning,
        false
      );
      return;
    }

    const columnasBackend = this.columnasEditando.map((c, index) => ({
      NombreVariable: c.NombreVariable,
      Etiqueta: c.Etiqueta?.trim() || c.NombreVariable,
      Orden: index,
      VisibleEnTabla: c.VisibleEnTabla,
      TipoControl: c.TipoControl,
      EsCampoNativo: c.EsCampoNativo,
      ColumnaReferenciaVista: c.ColumnaReferenciaVista || undefined
    }));

    if (columnasBackend.length === 0) {
      this.baseService.triggerShowModal(
        'Validación',
        'Debe seleccionar al menos una variable/columna para guardar la configuración en backend.',
        ModalNotifyTypes.Warning,
        false
      );
      return;
    }

    this.botonProcesando = true;
    try {
      const payload: any = {
        IdProceso: this.idProcesoSeleccionado,
        TipoOrigen: this.tipoOrigen,
        NombreVista: this.tipoOrigen === 'Vista' ? this.vistaSeleccionada : undefined,
        HabilitarBusquedaFechas: this.habilitarBusquedaFechas,
        ColumnaFechaVista: this.tipoOrigen === 'Vista' ? (this.columnaFechaVista || undefined) : undefined,
        HabilitarHistorico: this.habilitarHistorico,
        ModoHistorico: this.tipoOrigen === 'Vista' ? 'sp' : this.modoHistorico,
        ConfiguracionHistoricoSp: this.habilitarHistorico && this.modoHistorico === 'sp' ? this.historialSP : [],
        Columnas: columnasBackend
      };

      const response = await this.reporteriaService.guardarConfiguracion(payload);

      if (!response.Ok) {
        let mensajeError = '<ul>';
        (response.ValidationErrors || []).forEach((e: string) => { mensajeError += `<li>${e}</li>`; });
        mensajeError += '</ul>';
        this.baseService.triggerShowModal('Error al guardar', mensajeError, ModalNotifyTypes.Danger, false);
      } else {
        this.guardarFrontendMeta();
        this.safeCloseDialog();
        this.baseService.triggerShowModal(
          'Configuración guardada',
          'La configuración de reportería se guardó correctamente.',
          ModalNotifyTypes.Success,
          false
        );
      }
    } catch (error: any) {
      console.error('Error al guardar configuración de reportería:', error);
      this.baseService.triggerShowModal('Error', error?.error?.Error || 'Error inesperado al guardar.', ModalNotifyTypes.Danger, false);
    } finally {
      this.botonProcesando = false;
    }
  }

  cerrarModalConfig(): void {
    this.safeCloseDialog();
  }

  getVisibleEnTabla(nombreVariable: string): boolean {
    const columna = this.obtenerColumna(nombreVariable);
    return columna ? columna.VisibleEnTabla : true;
  }

  setVisibleEnTabla(nombreVariable: string, visible: boolean): void {
    const columna = this.obtenerColumna(nombreVariable);
    if (!columna) return;
    columna.VisibleEnTabla = visible;
  }

  getTipoControl(nombreVariable: string): 'input' | 'areatext' {
    const columna = this.obtenerColumna(nombreVariable);
    return columna ? columna.TipoControl : 'input';
  }

  setTipoControl(nombreVariable: string, tipoControl: 'input' | 'areatext'): void {
    const columna = this.obtenerColumna(nombreVariable);
    if (!columna) return;
    columna.TipoControl = tipoControl;
  }

  getColumnaReferencia(nombreVariable: string): string {
    const columna = this.obtenerColumna(nombreVariable);
    return columna?.ColumnaReferenciaVista || '';
  }

  setColumnaReferencia(nombreVariable: string, referencia: string): void {
    const columna = this.obtenerColumna(nombreVariable);
    if (!columna) return;
    columna.ColumnaReferenciaVista = referencia;
  }

  esCampoNativo(nombreVariable: string): boolean {
    return nombreVariable.startsWith('__SYS_') || NOMBRES_NATIVOS_CONOCIDOS.has(nombreVariable);
  }

  // Triple respaldo: el booleano EsCampoNativo puede venir incorrecto/omitido desde el backend en
  // algunos casos; Origen === 'Nativo' es la señal más confiable, y el nombre conocido es el último
  // respaldo. Cualquiera de las tres en true es suficiente para tratarlo como campo nativo.
  esCampoNativoVariable(variable: CatalogoVariableReporteDTO): boolean {
    return !!variable.EsCampoNativo || variable.Origen === 'Nativo' || this.esCampoNativo(variable.NombreVariable);
  }

  hayReferenciasFaltantes(): boolean {
    return this.tipoOrigen === 'Vista' &&
      this.columnasEditando.some(c => c.EsCampoNativo && !c.ColumnaReferenciaVista?.trim());
  }

  obtenerNombreLegible(nombreVariable: string): string {
    const legibles: Record<string, string> = {
      '__SYS_ID_EJECUCION': 'Id Ejecución',
      '__SYS_ESTADO': 'Estado',
      '__SYS_TIPO': 'Tipo',
      '__SYS_USUARIO_INICIA': 'Usuario inicia',
      '__SYS_FECHA_INICIA': 'Fecha inicia',
      '__SYS_FECHA_FINALIZA': 'Fecha finaliza',
      'IdEjecucion': 'Id Ejecución',
      'Estado': 'Estado',
      'Tipo': 'Tipo',
      'UsuarioInicia': 'Usuario inicia',
      'FechaInicia': 'Fecha inicia',
      'FechaFinaliza': 'Fecha finaliza'
    };
    return legibles[nombreVariable] || nombreVariable;
  }

  private obtenerEtiquetaDefault(nombreVariable: string): string {
    return this.obtenerNombreLegible(nombreVariable);
  }

  private obtenerColumna(nombreVariable: string): ColumnaEditando | undefined {
    return this.columnasEditando.find(c => c.NombreVariable === nombreVariable);
  }

  private hidratarColumnasEditando(
    columnasBackend: ConfiguracionReporteColumnaResponseDTO[],
    metaFrontend: ReporteriaProcesoFrontendMeta | null
  ): void {
    const catalogoMap = new Map(this.catalogoVariables.map(v => [v.NombreVariable, v]));
    const columnasFinales: ColumnaEditando[] = [];
    const nombresIncluidos = new Set<string>();

    const columnasBackendOrdenadas = [...columnasBackend].sort((a, b) => a.Orden - b.Orden);
    const backendMap = new Map(columnasBackendOrdenadas.map(c => [c.NombreVariable, c]));

    if (metaFrontend?.Columnas?.length) {
      const metaOrdenada = [...metaFrontend.Columnas].sort((a, b) => a.Orden - b.Orden);
      metaOrdenada.forEach(metaCol => {
        if (!catalogoMap.has(metaCol.NombreVariable)) return;
        if (!this.esCampoNativo(metaCol.NombreVariable) && !backendMap.has(metaCol.NombreVariable)) return;

        const refCatalogo = catalogoMap.get(metaCol.NombreVariable)!;
        columnasFinales.push({
          NombreVariable: metaCol.NombreVariable,
          TipoDato: refCatalogo.TipoDato,
          Origen: refCatalogo.Origen,
          Etiqueta: metaCol.Etiqueta || this.obtenerEtiquetaDefault(metaCol.NombreVariable),
          Orden: columnasFinales.length,
          VisibleEnTabla: metaCol.VisibleEnTabla !== false,
          TipoControl: metaCol.TipoControl || 'input',
          EsCampoNativo: !!metaCol.EsCampoNativo || !!refCatalogo.EsCampoNativo || refCatalogo.Origen === 'Nativo' || this.esCampoNativo(metaCol.NombreVariable),
          ColumnaReferenciaVista: metaCol.ColumnaReferenciaVista || ''
        });
        nombresIncluidos.add(metaCol.NombreVariable);
      });
    }

    columnasBackendOrdenadas.forEach(col => {
      if (nombresIncluidos.has(col.NombreVariable)) return;
      const refCatalogo = catalogoMap.get(col.NombreVariable);
      if (!refCatalogo) return;
      columnasFinales.push({
        NombreVariable: col.NombreVariable,
        TipoDato: refCatalogo.TipoDato,
        Origen: refCatalogo.Origen,
        Etiqueta: col.Etiqueta || this.obtenerEtiquetaDefault(col.NombreVariable),
        Orden: columnasFinales.length,
        VisibleEnTabla: col.VisibleEnTabla !== false,
        TipoControl: col.TipoControl || 'input',
        EsCampoNativo: !!col.EsCampoNativo || !!refCatalogo.EsCampoNativo || refCatalogo.Origen === 'Nativo' || this.esCampoNativo(col.NombreVariable),
        ColumnaReferenciaVista: col.ColumnaReferenciaVista || ''
      });
    });

    this.columnasEditando = columnasFinales;
  }

  private obtenerStorageKey(): string {
    return `bsp_reporteria_front_meta_${this.idProcesoSeleccionado}`;
  }

  private obtenerFrontendMeta(): ReporteriaProcesoFrontendMeta | null {
    try {
      const raw = localStorage.getItem(this.obtenerStorageKey());
      if (!raw) return null;
      return JSON.parse(raw) as ReporteriaProcesoFrontendMeta;
    } catch {
      return null;
    }
  }

  private guardarFrontendMeta(): void {
    const meta: ReporteriaProcesoFrontendMeta = {
      TipoOrigen: this.tipoOrigen,
      NombreVista: this.tipoOrigen === 'Vista' ? this.vistaSeleccionada : undefined,
      HabilitarBusquedaFechas: this.habilitarBusquedaFechas,
      ColumnaFechaVista: this.tipoOrigen === 'Vista' ? (this.columnaFechaVista || undefined) : undefined,
      HabilitarHistorico: this.habilitarHistorico,
      ModoHistorico: this.tipoOrigen === 'Vista' ? 'sp' : this.modoHistorico,
      ConfiguracionHistoricoSp: this.habilitarHistorico ? this.historialSP : [],
      Columnas: this.columnasEditando.map((c, index): ReporteriaColumnaFrontendMeta => ({
        NombreVariable: c.NombreVariable,
        Etiqueta: c.Etiqueta,
        Orden: index,
        VisibleEnTabla: c.VisibleEnTabla,
        TipoControl: c.TipoControl,
        EsCampoNativo: c.EsCampoNativo,
        ColumnaReferenciaVista: c.ColumnaReferenciaVista || undefined
      }))
    };
    localStorage.setItem(this.obtenerStorageKey(), JSON.stringify(meta));
  }

  private safeCloseDialog(): void {
    try {
      this.dialogComponentConfig?.close();
    } catch {
      // Fabric removeChild can fail if the dialog element has already been
      // detached from the DOM (e.g. closed programmatically twice).
    } finally {
      this.dialogComponentConfig = null;
    }
  }
}
